export const LIBRARY_PATH = "/fortune-100-prompt-library";
export const SITE_ORIGIN = "https://www.technical-leaders.com";
export const SHARE_CAMPAIGN = "prompt-library-sharing";
export const SHARE_CHANNELS = ["copy_link", "linkedin", "email", "native", "suggested_message"] as const;
export type ShareChannel = typeof SHARE_CHANNELS[number];
export type ShareWorkflow = { slug: string; title: string; purpose: string; claudeOnly?: boolean };

export const workflowPath = (slug: string) => `${LIBRARY_PATH}/${slug}`;
export const shareImage = (slug = "library") => `${SITE_ORIGIN}/social/prompt-library/${slug}.png`;

export function shareUrl(channel: ShareChannel, workflow?: ShareWorkflow) {
  const url = new URL(workflow ? workflowPath(workflow.slug) : LIBRARY_PATH, SITE_ORIGIN);
  url.search = new URLSearchParams({
    utm_source: channel,
    utm_medium: "referral",
    utm_campaign: SHARE_CAMPAIGN,
    utm_content: workflow?.slug || "library",
  }).toString();
  return url.href;
}

export function suggestedMessage(url: string, workflow?: ShareWorkflow) {
  return workflow
    ? `Thought this might be useful: ${workflow.title}. ${workflow.purpose}\n\nInstructions here: ${url}`
    : `A useful collection of AI workflows for everyday work. Copy-ready instructions for tasks you already do.\n\nFind one for your team: ${url}`;
}

const REFERRAL_KEY = "technical-leaders:prompt-library-referral:v1";
const MAX_AGE = 24 * 60 * 60 * 1000;
type Referral = { channel: string; workflow: string; at: number };

export function parseShareReferral(search: string, now = Date.now()): Referral | null {
  const params = new URLSearchParams(search);
  const channel = params.get("utm_source") || "";
  const workflow = params.get("utm_content") || "library";
  if (params.get("utm_campaign") !== SHARE_CAMPAIGN ||
      params.get("utm_medium") !== "referral" ||
      !SHARE_CHANNELS.includes(channel as ShareChannel) ||
      !/^[a-z0-9-]{1,100}$/.test(workflow)) return null;
  return { channel, workflow, at: now };
}

// Keep attribution within this browser tab for the workshop journey. Share URLs
// never copy the visitor's incoming parameters or contain a personal identifier.
export function rememberShareReferral(search: string) {
  const referral = parseShareReferral(search);
  if (!referral || typeof window === "undefined") return;
  try { window.sessionStorage.setItem(REFERRAL_KEY, JSON.stringify(referral)); } catch { /* Optional storage. */ }
}

export function getShareReferralProperties() {
  if (typeof window === "undefined") return {};
  let referral = parseShareReferral(window.location.search);
  if (!referral) {
    try {
      const stored: Referral = JSON.parse(window.sessionStorage.getItem(REFERRAL_KEY) || "null");
      if (stored && Number.isFinite(stored.at) && Date.now() >= stored.at && Date.now() - stored.at < MAX_AGE &&
          SHARE_CHANNELS.includes(stored.channel as ShareChannel) && /^[a-z0-9-]{1,100}$/.test(stored.workflow)) referral = stored;
    } catch { /* Optional storage. */ }
  }
  return referral ? {
    share_referred: true,
    share_referral_channel: referral.channel,
    share_referral_workflow: referral.workflow,
  } : {};
}
